import { useState, useCallback } from 'react';
import { submitForm as apiSubmitForm, UploadedFileInfo } from '../services/api';
import { CustomerType, OnboardingFormData } from '../types';
import { transformSubmitData } from '../utils/transformSubmitData';

export interface UseFormSubmissionReturn {
  isSubmitting: boolean;
  isSubmitted: boolean;
  submissionId: string | null;
  submitError: string | null;
  submitErrorType: 'TOKEN_EXPIRED' | 'TOKEN_CONSUMED' | null;
  handleSubmit: () => Promise<void>;
}

/**
 * Hook para gerenciar o envio do formulário.
 * Draft persistence é responsabilidade de useDraftPersistence.
 */
export function useFormSubmission(
  token: string | null,
  formData: Partial<OnboardingFormData>,
  customerType: CustomerType,
  uploadedFiles: Record<string, UploadedFileInfo>
): UseFormSubmissionReturn {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitErrorType, setSubmitErrorType] = useState<'TOKEN_EXPIRED' | 'TOKEN_CONSUMED' | null>(null);

  const handleSubmit = useCallback(async () => {
    if (!token) {
      setSubmitError('Token não encontrado');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitErrorType(null);

    try {
      const submitData = transformSubmitData(token, formData, customerType, uploadedFiles);
      const response = await apiSubmitForm(submitData);

      if (response.success) {
        setIsSubmitted(true);
        setSubmissionId(response.submission_id || null);
      } else {
        if (response.error === 'TOKEN_EXPIRED') {
          setSubmitError('O link de acesso expirou durante o preenchimento.');
          setSubmitErrorType('TOKEN_EXPIRED');
        } else if (response.error === 'TOKEN_CONSUMED') {
          setSubmitError('Este formulário já foi enviado anteriormente.');
          setSubmitErrorType('TOKEN_CONSUMED');
        } else {
          setSubmitError(response.message || 'Erro ao enviar formulário');
        }
      }
    } catch {
      setSubmitError('Erro ao enviar o formulário. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  }, [token, formData, customerType, uploadedFiles]);

  return {
    isSubmitting,
    isSubmitted,
    submissionId,
    submitError,
    submitErrorType,
    handleSubmit,
  };
}
